/**
 * LegislatorsPage
 * 
 * Main legislators directory page. Uses shared legislator components.
 * Features: Search, filters, bulk selection, bookmark to workspace, table/card views.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Loader2,
  ArrowUp,
  Users,
  Plus,
  Minus
} from 'lucide-react';
import LegislatorWorkspaceSelectionModal from '../components/LegislatorWorkspaceSelectionModal';
import BulkWorkspaceSelectionModal from '../components/BulkWorkspaceSelectionModal';
import Button from '../components/Button';
import RecordCount from '../components/RecordCount';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMobile } from '../hooks/useMediaQuery';
import api from '../lib/api';

// Shared legislator components
import {
  BaseLegislator,
  DEFAULT_FILTER_STATE as LEG_DEFAULT_FILTER_STATE,
  LegislatorRenderContext,
  ViewMode,
  useLegislatorFiltering,
  useLegislatorData,
  useTrackedLegislatorIds,
  LegislatorFilters,
  LegislatorCardGrid,
} from '../components/legislators';
import DataGrid from '../components/shared/DataGrid';
import { prefetchDistrictMapsForRows } from '../components/shared/dataGridCells';
import { PreviewIcon, TrackIcon } from '../components/icons';
import type { ActionsHelpIcon } from '../components/ActionsHeaderHelp';
import { buildLegislatorColumns, LEGISLATOR_SORT_FIELD_MAP } from '../components/legislators/legislatorColumnDefs';
import { createBillFilterUrl } from '../utils/billStatusMapping';
import ViewToggle from '../components/ViewToggle';
import EmptyState from '../components/EmptyState';

// =============================================================================
// Component
// =============================================================================

const LegislatorsPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  
  // Set page title
  usePageTitle('Legislators', 'Browse and filter Colorado state legislators');

  // View mode with persistence
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('legislatorsViewMode');
    return (saved === 'card' || saved === 'table') ? saved : 'table';
  });

  useEffect(() => {
    localStorage.setItem('legislatorsViewMode', viewMode);
  }, [viewMode]);

  // Shared filtering hook
  const {
    filters,
    updateFilter,
    clearFilters,
    debouncedSearchQuery,
    sort,
    setSort,
    filterOptions,
    isLoadingOptions,
    applySortToData,
  } = useLegislatorFiltering({
    debounceDelay: 300,
  });

  // Data fetching with server-side filtering
  const { data: legislators, isLoading, totalCount } = useLegislatorData<BaseLegislator>({
    source: 'all',
    filters: {
      ...filters,
      searchQuery: debouncedSearchQuery,
    },
    sort,
  });

  // Batch-resolve district maps so each row/card DistrictMapLink reads from cache
  // instead of firing its own GET /district-maps (N+1). Both table and card views
  // render district links here.
  useEffect(() => {
    prefetchDistrictMapsForRows(legislators);
  }, [legislators]);

  // Tracked legislators for bookmark status
  const { trackedIds: trackedLegislatorIds, refetch: refetchTracked } = useTrackedLegislatorIds();

  // Total unfiltered count (for the record count bar)
  const { data: totalLegislatorCount = 0 } = useQuery<number>(
    ['legislators-total-count', filters.sessionYear],
    async () => {
      const params = new URLSearchParams({ page: '1', limit: '1' });
      if (filters.sessionYear) params.append('session_year', filters.sessionYear);
      const response = await api.get(`/legislators?${params.toString()}`);
      return response.data.data?.pagination?.total ?? 0;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const hasActiveFilters = useMemo(() => {
    return (
      (debouncedSearchQuery || '') !== '' ||
      (filters.chamber !== 'All' && filters.chamber !== LEG_DEFAULT_FILTER_STATE.chamber) ||
      (filters.committee !== 'All committees' && filters.committee !== LEG_DEFAULT_FILTER_STATE.committee) ||
      (filters.party !== 'All parties' && filters.party !== LEG_DEFAULT_FILTER_STATE.party) ||
      (filters.district !== 'All districts' && filters.district !== LEG_DEFAULT_FILTER_STATE.district)
    );
  }, [debouncedSearchQuery, filters]);

  // Selection state
  const [selectedLegislatorIds, setSelectedLegislatorIds] = useState<Set<number>>(new Set());

  // Bookmark modal state
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [selectedLegislator, setSelectedLegislator] = useState<{ id: number; name: string } | null>(null);
  const [showBulkBookmarkModal, setShowBulkBookmarkModal] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'add' | 'remove'>('add');

  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Client-side sort (since server returns all matching results)
  const sortedLegislators = useMemo(() => {
    return applySortToData(legislators);
  }, [legislators, applySortToData]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleBookmarkClick = useCallback((legislatorId: number, legislatorName: string) => {
    setSelectedLegislator({ id: legislatorId, name: legislatorName });
    setShowBookmarkModal(true);
  }, []);

  const handleLegislatorSelect = useCallback((legislatorId: number, checked: boolean) => {
    setSelectedLegislatorIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(legislatorId);
      } else {
        newSet.delete(legislatorId);
      }
      return newSet;
    });
  }, []);

  const handleBulkBookmark = useCallback((actionType: 'add' | 'remove') => {
    setBulkActionType(actionType);
    setShowBulkBookmarkModal(true);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLegislatorIds(new Set());
  }, []);

  const handleNavigate = useCallback((legislatorId: number) => {
    navigate(`/legislators/${legislatorId}`);
  }, [navigate]);

  // Scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.scrollTop > 0) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      }
    }
  }, []);

  const handleScroll = useCallback(() => {
    const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    let containerScrollTop = 0;
    
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
        containerScrollTop = container.scrollTop;
        break;
      }
    }
    
    const totalScroll = Math.max(windowScrollTop, containerScrollTop);
    setShowScrollToTop(totalScroll > 300);
  }, []);

  // Scroll listener setup
  useEffect(() => {
    const setupScrollListeners = () => {
      window.addEventListener('scroll', handleScroll);
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    let mainContainer = setupScrollListeners();
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    
    if (!mainContainer) {
      retryTimer = setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [handleScroll, legislators]);

  // ==========================================================================
  // Render Context
  // ==========================================================================

  const renderContext: LegislatorRenderContext = useMemo(() => ({
    onNavigate: handleNavigate,
    onBookmark: handleBookmarkClick,
    trackedLegislatorIds,
    selectedLegislatorIds,
    onSelectLegislator: handleLegislatorSelect,
  }), [handleNavigate, handleBookmarkClick, trackedLegislatorIds, selectedLegislatorIds, handleLegislatorSelect]);

  // AG Grid column definitions (for DataGrid)
  const gridColumnDefs = useMemo(() => buildLegislatorColumns({
    columns: ['name', 'chamber', 'district', 'party', 'committees', 'sponsored_bills', 'contact', 'actions'],
    mode: 'directory',
  }), []);

  const gridContext = useMemo(() => ({
    onNavigate: handleNavigate,
    onBookmark: handleBookmarkClick,
    onNavigateBills: (row: any) => {
      const url = createBillFilterUrl(String(row.id), undefined, { sessionYear: row.sessionYear || row.session_year });
      navigate(url, { state: { returnUrl: window.location.pathname + window.location.search } });
    },
    trackedLegislatorIds,
    permissions: {},
    actions: [
      {
        id: 'view',
        icon: PreviewIcon,
        title: 'View details',
        className: 'text-text-tertiary hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary',
        onClick: (row: any) => handleNavigate(row.id),
      },
      {
        id: 'bookmark',
        icon: TrackIcon,
        title: 'Track legislator',
        className: 'text-text-tertiary hover:text-blue-600 dark:text-text-dark-tertiary',
        activeClassName: 'text-blue-600 hover:text-blue-700',
        isActive: (row: any) => trackedLegislatorIds.has(row.id),
        onClick: (row: any) => handleBookmarkClick(row.id, row.fullName || row.full_name || ''),
      },
    ],
    actionsHelpIcons: ['view', 'track'] satisfies ActionsHelpIcon[],
  }), [handleNavigate, handleBookmarkClick, trackedLegislatorIds, navigate]);

  const handleGridSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSort({ field: field as any, direction });
  }, [setSort]);

  // ==========================================================================
  // Render
  // ==========================================================================

  /*
    The header session chip was removed 2026-08-27 (with the whole chip
    pattern): it duplicated the filter panel's Session dropdown, which is now
    the single session control and displays the CURRENT_SESSION default as the
    real current session.
  */
  return (
    <div className="space-y-6 pt-2">
      {/* Search and Filters */}
      {isMobile ? (
        // Mobile: Simple search only
        <LegislatorFilters
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={updateFilter}
          onClearFilters={clearFilters}
          config={{
            showSearch: true,
            showSessionYear: false,
            showChamber: false,
            showCommittee: false,
            showParty: false,
            showDistrict: false,
            showClearButton: false,
            showSearchButton: false,
            compact: true,
          }}
          showCard={false}
          isLoadingOptions={isLoadingOptions}
        />
      ) : (
        // Desktop: Full filters
        <LegislatorFilters
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={updateFilter}
          onClearFilters={clearFilters}
          config={{
            showSearch: true,
            showSessionYear: true,
            showChamber: true,
            showCommittee: true,
            showParty: true,
            showDistrict: true,
            showClearButton: true,
            showSearchButton: false, // Filtering is reactive
          }}
          isLoadingOptions={isLoadingOptions}
        />
      )}

      {/* View Toggle and Results */}
      <div className="flex justify-between items-center">
        <div className="hidden lg:flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <RecordCount
          label="legislators"
          showing={legislators.length}
          filtered={totalCount}
          total={totalLegislatorCount}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">Loading legislators...</span>
        </div>
      ) : legislators.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No legislators found"
          hint="Try adjusting your filters or search terms."
        />
      ) : (
        <>
          {/* Bulk Selection Controls */}
          {selectedLegislatorIds.size > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {selectedLegislatorIds.size} legislator{selectedLegislatorIds.size !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleBulkBookmark('add')}
                    variant="primary"
                    size="sm"
                    icon={Plus}
                  >
                    Add to Workspaces
                  </Button>
                  <Button
                    onClick={() => handleBulkBookmark('remove')}
                    variant="danger"
                    size="sm"
                    icon={Minus}
                  >
                    Remove from Workspaces
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile: Card View Only */}
          <div className="md:hidden">
            <LegislatorCardGrid
              data={sortedLegislators}
              context={renderContext}
              config={{
                showContact: true,
                showCommittees: true,
                showSponsoredBills: true,
                showVotingRecords: true,
              }}
              gridCols="grid-cols-1"
            />
          </div>

          {/* Desktop: Table or Card View */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <DataGrid
                data={sortedLegislators}
                columnDefs={gridColumnDefs}
                gridContext={gridContext}
                getRowId={(params) => String(params.data.id)}
                sortBy={sort.field}
                sortDirection={sort.direction}
                sortFieldMap={LEGISLATOR_SORT_FIELD_MAP}
                onSort={handleGridSort}
                onRowClick={(legislator) => handleNavigate(legislator.id)}
                selectable={true}
                selectedIds={new Set(Array.from(selectedLegislatorIds).map(String))}
                onSelectionChanged={(ids) => setSelectedLegislatorIds(new Set(Array.from(ids).map(Number)))}
                emptyMessage="No legislators found"
              />
            ) : (
              <LegislatorCardGrid
                data={sortedLegislators}
                context={renderContext}
                config={{
                  showContact: true,
                  showCommittees: true,
                  showSponsoredBills: true,
                  showVotingRecords: true,
                }}
                gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              />
            )}
          </div>
        </>
      )}

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Legislator Workspace Selection Modal */}
      <LegislatorWorkspaceSelectionModal
        isOpen={showBookmarkModal}
        onClose={() => setShowBookmarkModal(false)}
        legislatorId={selectedLegislator?.id?.toString() || ''}
        legislatorName={selectedLegislator?.name || ''}
        onUpdate={() => {
          refetchTracked();
        }}
      />

      {/* Bulk Legislator Workspace Selection Modal */}
      <BulkWorkspaceSelectionModal
        isOpen={showBulkBookmarkModal}
        onClose={() => {
          setShowBulkBookmarkModal(false);
          clearSelection();
        }}
        entityType="legislator"
        entityCount={selectedLegislatorIds.size}
        actionType={bulkActionType}
        onSave={async (workspaceIds) => {
          await api.post('/workspace-legislator-tracking/bulk', {
            legislatorIds: Array.from(selectedLegislatorIds),
            workspaceIds,
            action: bulkActionType,
            isPriority: false,
            notes: '',
          });
          refetchTracked();
        }}
      />
    </div>
  );
};

export default LegislatorsPage;
